<template>
  <div>
    <div class="mb-8 appointment-details-wrapper">
      <appointment-action-group
        :appointment="appointment"
        :appointment-actions="headerActions"></appointment-action-group>
    </div>

    <v-card class="appointment-details-wrapper guest-appointment-wrapper">
      <v-card-title>
        <status-manager
          class="full-width"
          :appointment="appointment"
          :should-edit-status="false"
          dense>
        </status-manager>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row class="black--text">
            <v-col class="column-size">
              <v-row>
                <v-col class="d-flex">
                  <v-icon class="text-lg-h5 mr-2" size="32"> mdi-calendar</v-icon>
                  <div class="header-text text-h6 font-weight-regular">
                    {{ apptDate }}
                    <span class="mx-2 d-inline-block>">&bull;</span>
                    {{ getAppointmentInterval(appointment) }}
                  </div>
                </v-col>
              </v-row>

              <template v-if="!loading">
                <template v-for="(row, index) in appointmentTableRows">
                  <template v-if="!row.isHidden">
                    <v-row :key="index + '-detail'" class="mt-4">
                      <template>
                        <v-col class="font-weight-bold col-12 col-md-4 py-0">
                          {{ row.label }}
                        </v-col>
                        <v-col class="col-12 col-md-8 pt-2 pt-md-0">
                          <div v-html="row.value"></div>
                        </v-col>
                      </template>
                    </v-row>
                  </template>
                </template>

                <template v-for="customField in Object.values(customFields)">
                  <v-row v-if="showCustomField(customField)" :key="customField.key" class="mt-4">
                    <v-col class="font-weight-bold col-12 col-md-4 py-0">
                      <help-icon-tooltip
                        v-if="customField.requiredForCheckIn"
                        icon-name="mdi-map-marker"
                        icon-style-class="grey darken-2">
                        Required for self check in
                      </help-icon-tooltip>
                      {{ customField.label }}
                    </v-col>
                    <v-col class="col-12 col-md-8 pt-2 pt-md-0" :class="customField.tdClass">
                      <div v-html="customField.value" class="text-pre-wrap"></div>
                    </v-col>
                  </v-row>
                </template>

                <v-row class="mt-4">
                  <v-col class="font-weight-bold col-12 col-md-4 py-0"> Appointment Notes:</v-col>
                  <v-col class="col-12 col-md-6 pt-2 pt-md-0">
                    <div v-html="appointment.notes || '----'"></div>
                  </v-col>
                </v-row>

                <v-row class="mt-4">
                  <v-col class="font-weight-bold col-12 col-md-4 py-0"> Confirmation #</v-col>
                  <v-col class="col-12 col-md-8 pt-2 pt-md-0">
                    <div>
                      <span class="mr-4">{{ appointment.confirmationNumber }}</span>
                      <secondary-button
                        v-if="canCopy"
                        x-small
                        @click="copyValueToClipboard(appointment.confirmationNumber)">
                        Copy
                      </secondary-button>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="mt-4">
                  <v-col class="font-weight-bold col-12 col-md-4 py-0"> Appointment ID</v-col>
                  <v-col class="col-12 col-md-8 pt-2 pt-md-0">
                    <div>
                      <span class="mr-4">{{ appointment.id }}</span>
                      <secondary-button v-if="canCopy" x-small @click="copyApptId">
                        Copy
                      </secondary-button>
                    </div>
                  </v-col>
                </v-row>
              </template>
            </v-col>

            <v-col class="d-flex justify-center divider-size pa-md-0">
              <v-divider
                :vertical="
                  $vuetify.breakpoint.width >= $vuetify.breakpoint.thresholds.md
                "></v-divider>
            </v-col>

            <v-col class="column-size">
              <div class="d-flex flex-column">
                <div>
                  <span class="font-weight-bold pb-2 d-flex align-center">
                    <v-icon class="mr-2">mdi-truck-outline</v-icon>
                    Carrier
                  </span>
                  <span class="d-block font-weight-bold pb-1">
                    {{ companyName }}
                  </span>
                  <span class="d-flex align-center pb-1 mt-2">
                    <v-icon class="mr-2" small>mdi-account</v-icon>
                    Name: {{ getUserName(appointment.user) }}
                  </span>
                  <span class="d-flex align-center pb-1 mt-2">
                    <v-icon class="mr-2" small>mdi-email</v-icon>
                    Email: {{ appointment.user.email }}
                  </span>
                  <span class="d-flex align-center pb-1 mt-2">
                    <v-icon class="mr-2" small>mdi-phone</v-icon>
                    Phone: {{ appointment.user.phone || '----' }}
                  </span>
                  <span class="d-flex align-center pb-1 mt-2">
                    <v-icon class="mr-2" small>mdi-phone</v-icon>
                    SCAC: {{ scac }}
                  </span>
                </div>

                <div>
                  <span class="font-weight-bold mt-12 pb-2 d-flex align-center">
                    <v-icon class="mr-2">mdi-warehouse</v-icon>
                    Warehouse
                  </span>
                  <span class="d-block font-weight-bold pb-1">
                    {{ appointment.dock.warehouse.name }}
                  </span>
                  <span class="d-flex align-center pb-1 mt-2">
                    <v-icon class="mr-2" small>mdi-account</v-icon>
                    <copy-content
                      :content="getWarehouseAddress(appointment.dock.warehouse)"
                      label="Click to copy warehouse address">
                      {{ getWarehouseAddress(appointment.dock.warehouse) }}
                    </copy-content>
                  </span>
                  <span class="d-flex align-center pb-1 mt-2">
                    <v-icon class="mr-2" small>mdi-email</v-icon>
                    {{ appointment.dock.warehouse.email }}
                  </span>
                  <span class="d-flex align-center pb-1 mt-2">
                    <v-icon class="mr-2" small>mdi-phone</v-icon>
                    {{ appointment.dock.warehouse.phone }}
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <template v-if="isCancelled">
        <div class="d-flex justify-center my-8">
          <v-btn
            elevation="0"
            class="text__error font-weight-bold"
            color="white"
            :disabled="isCancelled">
            <v-icon class="mr-2">{{ cancelButtonIcon }}</v-icon>
            {{ cancelAppointmentLabel }}
          </v-btn>
        </div>
      </template>

      <v-alert color="error" class="white--text" v-if="!appointment && !loading">
        Could not load appointment data
      </v-alert>

      <template v-if="loading">
        <v-card-text>
          Loading Appointment Data...
          <v-progress-linear color="secondary" indeterminate rounded height="8">
          </v-progress-linear>
        </v-card-text>
      </template>
    </v-card>

    <slot name="footer-actions"></slot>
  </div>
</template>

<script>
import appointmentMixin from '@/components/mixins/appointmentMixin';

/**
 * @displayName Appointment Details
 */
export default {
  name: 'appointment-details-public',
  mixins: [appointmentMixin],
  props: {
    showHeader: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Appointment to display details for
     */
    appointment: {
      type: Object,
      required: true
    },
    /**
     * Display status editing options
     */
    shouldEditStatus: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      tags: this.appointment.tags,
      headerActions: {
        print: {
          icon: 'printer',
          action: 'openPrintTab',
          show: true,
          tooltip: 'Print details'
        }
      },
      appointmentTableRows: {
        dockName: {
          label: 'Dock Name',
          value: this.appointment.dock.name,
          isHidden: false
        },
        loadType: {
          label: 'Load Type',
          value: '',
          isHidden: false
        },
        refNumber: {
          label: 'Reference Number',
          value: this.appointment.refNumber || '----',
          isHidden: false
        }
      }
    };
  },
  methods: {
    initializeRowValues() {
      this.appointmentTableRows.refNumber.isHidden =
        !this.$consolidatedSettings.referenceNumberIsVisible;
      this.appointmentTableRows.refNumber.label =
        this.$consolidatedSettings.referenceNumberDisplayName;
      this.appointmentTableRows.refNumber.value = this.appointment.refNumber || 'N/A';

      this.appointmentTableRows.loadType.value = this.$loadTypeName(
        this.appointment.loadType,
        false,
        true
      );
    }
  },
  computed: {
    apptDate() {
      return this.novaCore.formatDateTimeWithMilitarySupport(
        this.appointment.start,
        this.appointment.dock.warehouse.timezone,
        this.novaCore.LuxonDateTimeFormats.LongDateShortMonth,
        this.$isMilitaryTimeEnabled(this.$org), // TODO: Use Warehouse Setting (not available at this point)
        this.novaCore.LuxonDateTimeFormats.LongDateTime24ShortMonth
      );
    }
  },
  async mounted() {
    this.initializeRowValues();
    this.setCustomFieldsRows();
    this.loading = false;
  },
  watch: {
    appointment(appointment) {
      this.initializeRowValues();
      this.setCustomFieldsRows();
      this.tags = appointment.tags;
    }
  }
};
</script>

<style scoped lang="scss">
.column-size {
  max-width: 100%;
  flex-basis: 100%;

  @media (min-width: 960px) {
    max-width: 49%;
  }
}

.divider-size {
  max-width: 100%;
  flex-basis: 100%;

  @media (min-width: 960px) {
    max-width: 2%;
  }
}
</style>
